import { useState, useMemo } from 'react'
import { Target, Table, Send, CheckCircle2, Calendar } from 'lucide-react'
import { motion } from 'framer-motion'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import { categorizeDeal } from '@/utils/dealCategorization'
import { isRocketInstance } from '@/config/instanceProfile'
import { useDealsStore } from '@/stores/useDealsStore'
import type { Deal } from '@/types'
import { MetricsCards } from './MetricsCards'
import { BarChartSection } from './BarChartSection'
import { PieChartSection } from './PieChartSection'

interface GoalsDashboardProps {
  deals: Deal[]
}

type Period = 'monthly' | 'quarterly' | 'annual'

const PERIOD_LABELS: Record<Period, string> = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  annual: 'Anual',
}

const AGRADA_CATEGORY_LABELS: Record<string, string> = {
  marketing: 'Marketing',
  support: 'Suporte',
  project: 'Projeto',
}

const ROCKET_CATEGORY_LABELS: Record<string, string> = {
  rocket: 'Rocket Search',
}

const AGRADA_CATEGORY_KEYS = Object.keys(AGRADA_CATEGORY_LABELS)
const ROCKET_CATEGORY_KEYS = Object.keys(ROCKET_CATEGORY_LABELS)

const STAGE_LABELS: Record<string, string> = {
  qualificado: 'Qualificado',
  reuniao_agendada: 'Reunião Agendada',
  reuniao_realizada: 'Reunião Realizada',
  proposta_elaboracao: 'Proposta Elaboração',
  proposta_enviada: 'Proposta Enviada',
  teste_gratis: '30 dias Grátis',
  renegociacao: 'Renegociação',
  gerar_contrato: 'Gerar Contrato',
  ajustar_proposta: 'Ajustar Proposta',
  contrato_enviado: 'Contrato Enviado',
  ganho: 'Ganho',
  perdido: 'Perdido',
  pausado: 'Pausado',
  fechamento_futuro: 'Fech. Futuro',
}

function emptyCategoryBuckets(keys: string[]): Record<string, Deal[]> {
  const result: Record<string, Deal[]> = {}
  for (const key of keys) result[key] = []
  return result
}

function emptyAttainment(keys: string[]): Record<string, number> {
  const result: Record<string, number> = {}
  for (const key of keys) result[key] = 0
  return result
}

function emptyExpectation(keys: string[]) {
  const result: Record<string, { naMesa: number; enviado: number; total: number }> = {}
  for (const key of keys) result[key] = { naMesa: 0, enviado: 0, total: 0 }
  return result
}

function isInPeriod(deal: Deal, period: Period): boolean {
  const now = new Date()
  const date = new Date(deal.updatedAt)

  switch (period) {
    case 'monthly':
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
    case 'quarterly': {
      const qNow = Math.floor(now.getMonth() / 3)
      const qDate = Math.floor(date.getMonth() / 3)
      return qNow === qDate && date.getFullYear() === now.getFullYear()
    }
    case 'annual':
      return date.getFullYear() === now.getFullYear()
    default:
      return true
  }
}

export function GoalsDashboard({ deals }: GoalsDashboardProps) {
  const [period, setPeriod] = useState<Period>('monthly')
  const goalTargets = useDealsStore((s) => s.goalTargets)

  const categoryLabels = isRocketInstance ? ROCKET_CATEGORY_LABELS : AGRADA_CATEGORY_LABELS
  const categoryKeys = isRocketInstance ? ROCKET_CATEGORY_KEYS : AGRADA_CATEGORY_KEYS

  const periodDeals = useMemo(() => deals.filter((d) => isInPeriod(d, period)), [deals, period])

  const categorizedDeals = useMemo(() => {
    const result = emptyCategoryBuckets(categoryKeys)
    for (const deal of periodDeals) {
      // Na Rocket, todo deal conta na meta única "Rocket Search".
      const cat = isRocketInstance ? 'rocket' : categorizeDeal(deal)
      const bucket = cat ? result[cat] : undefined
      if (bucket) bucket.push(deal)
    }
    return result
  }, [periodDeals, categoryKeys])

  const attainment = useMemo(() => {
    const result = emptyAttainment(categoryKeys)
    for (const [cat, items] of Object.entries(categorizedDeals)) {
      if (!result[cat]) continue
      result[cat] = items.filter((d) => d.etapa === 'ganho').reduce((s, d) => s + d.valor, 0)
    }
    return result
  }, [categorizedDeals, categoryKeys])

  const expectation = useMemo(() => {
    const naMesaStages = ['proposta_elaboracao', 'proposta_enviada', 'renegociacao']
    const enviadoStages = ['contrato_enviado']
    const result = emptyExpectation(categoryKeys)
    for (const [cat, items] of Object.entries(categorizedDeals)) {
      if (!result[cat]) continue
      const naMesa = items
        .filter((d) => naMesaStages.includes(d.etapa))
        .reduce((s, d) => s + d.valor, 0)
      const enviado = items
        .filter((d) => enviadoStages.includes(d.etapa))
        .reduce((s, d) => s + d.valor, 0)
      result[cat] = { naMesa, enviado, total: naMesa + enviado }
    }
    return result
  }, [categorizedDeals, categoryKeys])

  const periodGoals = goalTargets[period] ?? {}

  const barData = useMemo(() => {
    return categoryKeys.map((cat) => ({
      name: categoryLabels[cat],
      key: cat,
      realizado: attainment[cat] ?? 0,
      meta: periodGoals[cat] ?? 0,
      expectation: expectation[cat]?.total ?? 0,
    }))
  }, [attainment, expectation, categoryKeys, categoryLabels, periodGoals])

  const pieData = useMemo(() => {
    const stageMap = new Map<string, number>()
    for (const deal of periodDeals) {
      const label = STAGE_LABELS[deal.etapa] ?? deal.etapa
      stageMap.set(label, (stageMap.get(label) ?? 0) + deal.valor)
    }
    return Array.from(stageMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
  }, [periodDeals])

  const wonDeals = periodDeals.filter((d) => d.etapa === 'ganho')
  const wonTotal = wonDeals.reduce((s, d) => s + d.valor, 0)
  const naMesaTotal = Object.values(expectation).reduce((s, e) => s + e.naMesa, 0)
  const enviadoTotal = Object.values(expectation).reduce((s, e) => s + e.enviado, 0)

  return (
    <div className="space-y-5">
      {/* Header with Period Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-text-primary font-bold text-lg flex items-center gap-2">
          <Target size={18} className="text-accent" />
          Performance do Pipeline
        </h2>
        <div className="flex items-center gap-1 bg-white/[0.03] p-1 rounded-lg border border-white/[0.06]">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                period === p
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
              )}
            >
              <Calendar size={12} />
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      <MetricsCards deals={periodDeals} />

      {/* Accumulated Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          className="glass-card p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-text-label uppercase tracking-wider font-medium" style={{ fontSize: 'var(--font-size-label)' }}>
              Na Mesa
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-violet-500/10">
              <Table size={14} className="text-violet-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-text-primary">{formatCurrencyCompact(naMesaTotal)}</p>
          <p className="text-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
            Propostas em elaboração, enviadas e renegociação
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.3 }}
          className="glass-card p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-text-label uppercase tracking-wider font-medium" style={{ fontSize: 'var(--font-size-label)' }}>
              Enviado
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-sky-500/10">
              <Send size={14} className="text-sky-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-text-primary">{formatCurrencyCompact(enviadoTotal)}</p>
          <p className="text-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
            Contratos enviados para assinatura
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          className="glass-card p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-text-label uppercase tracking-wider font-medium" style={{ fontSize: 'var(--font-size-label)' }}>
              Realizado (Attainment)
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-500/10">
              <CheckCircle2 size={14} className="text-emerald-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-text-primary">{formatCurrencyCompact(wonTotal)}</p>
          <p className="text-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
            {wonDeals.length} deals ganhos no período
          </p>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarChartSection
          period={period}
          periodLabel={PERIOD_LABELS[period]}
          barData={barData}
          attainment={attainment}
          chartTitle={
            isRocketInstance ? 'Realizado vs Meta — Rocket Search' : 'Realizado vs Meta por Categoria'
          }
        />
        <PieChartSection pieData={pieData} dealCount={periodDeals.length} />
      </div>
    </div>
  )
}
